/**
 * PickerPagingFooter — the prev/next strip shared by the V2 pickers.
 *
 * WHY THIS IS ITS OWN FILE
 * ------------------------
 * EntityPicker pages a list it already holds in memory. ApproverPicker pages a directory it must
 * never hold in memory, asking the server for one page at a time. The SOURCE differs and belongs
 * to each caller; the CHROME is identical, and identical chrome written twice is the fork
 * Architecture Principle 8 exists to prevent — the first off-by-one or disabled-state fix lands in
 * one copy and the two drift apart forever. So the callers own the paging state and this owns how
 * it looks.
 *
 * Renders nothing when there is only one page. A control that cannot do anything is noise, and it
 * is worse than noise in a dropdown, where every row of chrome is a row of results not shown.
 *
 * Props
 *   page       0-based index of the page on screen
 *   pageCount  total number of pages
 *   total      total number of matching items across all pages
 *   pageSize   items per page, used only to render the "31–40 of 412" range
 *   onPage(n)  asked to move to page n; the caller decides how (slice, or fetch)
 *   noun       plural noun for the range line, e.g. "people"
 */
import React from 'react';
import { ChevronLeft, ChevronRight } from 'lucide-react';

export function PickerPagingFooter({ page, pageCount, total, pageSize, onPage, noun = 'results' }) {
  const pages = Number(pageCount) || 0;
  if (pages <= 1) return null;

  const safePage = Math.min(Math.max(0, Number(page) || 0), pages - 1);
  const start = safePage * pageSize + 1;
  const end = Math.min((safePage + 1) * pageSize, total);
  const atStart = safePage <= 0;
  const atEnd = safePage >= pages - 1;

  const btn = 'px-1.5 py-1 rounded-v2-sm border border-v2-border inline-flex items-center '
    + 'disabled:opacity-40 disabled:cursor-default enabled:hover:border-v2-border-strong';

  return (
    <div className="shrink-0 flex items-center justify-between gap-2 px-3 py-2 border-t border-v2-border
      text-[12px] text-v2-ink-muted bg-v2-surface">
      <span className="truncate">{start}–{end} of {total} {noun}</span>
      <span className="flex items-center gap-1 shrink-0">
        <button
          type="button"
          className={btn}
          disabled={atStart}
          aria-label="Previous page"
          onClick={() => onPage?.(safePage - 1)}
        >
          <ChevronLeft size={13} />
        </button>
        <span className="min-w-[42px] text-center tabular-nums text-v2-ink">{safePage + 1} / {pages}</span>
        <button
          type="button"
          className={btn}
          disabled={atEnd}
          aria-label="Next page"
          onClick={() => onPage?.(safePage + 1)}
        >
          <ChevronRight size={13} />
        </button>
      </span>
    </div>
  );
}

export default PickerPagingFooter;
